"use client";

import { ArrowDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function () {
  const {
    ref: headlineRef,
    isVisible: headlineVisible
  } = useRevealOnScroll<HTMLHeadingElement>({
    threshold: 0.1
  });
  const {
    ref: kickerRef,
    isVisible: kickerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: imageRef,
    isVisible: imageVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: marqueeRef,
    isVisible: marqueeVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const headlineWords = [{
    text: "YOUR",
    italic: false
  }, {
    text: "STRATEGIC",
    italic: false
  }, {
    text: "NEGOTIATION",
    italic: true
  }, {
    text: "PARTNER.",
    italic: false
  }];
  const marqueeItems = ["EXPERT ADVOCACY", "MAXIMUM SAVINGS", "TRANSPARENT PROCESS", "CONSUMER PROTECTION", "PURCHASE POWER", "STRESS-FREE DEALS"];
  return <section data-section-id="4093" id="your-negotiation-partner" className="relative overflow-hidden bg-background text-foreground pt-24 pb-16 sm:pt-32 sm:pb-20 lg:pt-36 lg:pb-24">
      {}
      <div className="pointer-events-none absolute inset-0 opacity-[0.04] mix-blend-multiply" style={{
      backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='3'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")"
    }} aria-hidden="true" />

      <div className="relative mx-auto w-full max-w-[1400px] px-6 sm:px-8 lg:px-12">
        {}
        <div ref={kickerRef} className={`mb-10 flex items-center gap-3 transition-all duration-700 ease-out ${kickerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"}`}>
          <span className="font-default text-[11px] sm:text-xs uppercase tracking-[0.18em] text-foreground/70">
            EST. — Price Advocate Services
          </span>
          <span className="inline-block size-2 rotate-45 bg-accent" aria-hidden="true" />
          <span className="font-default text-[11px] sm:text-xs uppercase tracking-[0.18em] text-foreground/70">
            Advocacy
          </span>
        </div>

        {}
        <div className="grid grid-cols-1 lg:grid-cols-12 lg:gap-8">
          {}
          <div className="lg:col-span-7 lg:pr-4">
            <h1 ref={headlineRef} className="font-default font-bold leading-[0.92] tracking-tighter text-primary text-[64px] sm:text-[88px] md:text-[110px] lg:text-[128px] xl:text-[140px]">
              {headlineWords.map((word, i) => <span key={i} data-index={i} className="inline-block overflow-hidden align-bottom mr-[0.18em] last:mr-0">
                  <span className={`inline-block transition-transform duration-[900ms] ease-[cubic-bezier(0.22,1,0.36,1)] ${headlineVisible ? "translate-y-0" : "translate-y-full"} ${word.italic ? "italic text-primary font-bold" : ""}`} style={{
                transitionDelay: `${i * 80}ms`
              }}>
                    {word.text}
                  </span>
                </span>)}
            </h1>

            {}
            <div className={`mt-12 max-w-xl transition-all duration-700 ease-out ${headlineVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
            transitionDelay: "600ms"
          }}>
              <p className="font-default text-base sm:text-lg leading-relaxed text-foreground">
                We stand by your side to negotiate large purchases, ensuring you get the best value without the stress. Your advocate for fair pricing and smarter deals.
              </p>
              <div className="mt-8 flex flex-wrap items-center gap-4">
                <Button asChild size="lg" className="rounded-lg bg-accent text-white hover:bg-accent/90 font-default text-xs uppercase tracking-[0.15em] h-12 px-7">
                  <Link to="/PricingPage">Get Started</Link>
                </Button>
                <Button asChild variant="ghost" size="lg" className="rounded-lg text-primary border-2 border-primary hover:bg-primary hover:text-white font-default text-xs uppercase tracking-[0.15em] h-12 px-5">
                  <Link to="/HowItWorksPage">How It Works →</Link>
                </Button>
              </div>
            </div>
          </div>

          {}
          <div ref={imageRef} className={`relative lg:col-span-5 mt-12 lg:mt-20 transition-all duration-1000 ease-out ${imageVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`} style={{
          transitionDelay: "300ms"
        }}>
            <div className="relative overflow-hidden rounded-lg shadow-lg">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723817/img/negotiation_consultation.webp" alt="Professional consultant discussing purchase options with a client" loading="lazy" decoding="async" className="aspect-[4/5] w-full object-cover" />
            </div>

            {}
            <div className="absolute -bottom-6 -left-4 sm:-left-8 max-w-[260px] rounded-lg bg-white px-5 py-4 shadow-md border border-border">
              <div className="font-default text-[10px] sm:text-[11px] uppercase tracking-[0.16em] leading-relaxed text-foreground">
                <div className="text-muted-foreground">Success Rate</div>
                <div className="mt-1 text-primary font-bold text-lg">98%</div>
                <div className="mt-1 text-muted-foreground">Average Savings: 15%</div>
              </div>
            </div>
          </div>
        </div>

        {}
        <div ref={marqueeRef} className={`relative mt-32 sm:mt-40 lg:mt-48 overflow-hidden border-y border-border py-5 transition-opacity duration-700 ${marqueeVisible ? "opacity-100" : "opacity-0"}`}>
          <div className="flex w-max animate-[marquee_40s_linear_infinite] items-center gap-8">
            {[...marqueeItems, ...marqueeItems, ...marqueeItems].map((item, i) => <div key={i} data-index={i} className="flex items-center gap-8 shrink-0">
                <span className="font-default text-sm uppercase tracking-[0.2em] text-foreground/80 whitespace-nowrap">
                  {item}
                </span>
                <span className="inline-block size-1.5 rounded-lg bg-accent shrink-0" aria-hidden="true" />
              </div>)}
          </div>
        </div>

        {}
        <div className="mt-16 flex flex-col items-center gap-3">
          <span className="font-default text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
            Scroll
          </span>
          <div className="relative h-12 w-px bg-border overflow-hidden">
            <span className="absolute left-1/2 -translate-x-1/2 size-1.5 rounded-lg bg-accent animate-[scrollDot_2s_ease-in-out_infinite]" aria-hidden="true" />
          </div>
          <ArrowDown className="size-3.5 text-muted-foreground" strokeWidth={1.5} />
        </div>
      </div>

      <style>{`
        @keyframes marquee {
          from { transform: translateX(0); }
          to { transform: translateX(-33.333%); }
        }
        @keyframes scrollDot {
          0% { top: -10%; opacity: 0; }
          30% { opacity: 1; }
          100% { top: 100%; opacity: 0; }
        }
      `}</style>
    </section>;
}